// ============================================================
// excel.js — Geração do relatório Excel (ExcelJS)
//
// Função gerarExcel completa, extraída do App.js (Fase 1, Parte B)
// sem alterar nenhuma lógica de geração, fórmula ou estilo.
// ============================================================

import ExcelJS from "exceljs";
import {
  calcularFrequencia,
  classificarBloco,
  corDaModalidade,
  normalizarModalidade,
  TETO_JUSTIFICATIVA,
} from "./calculo";
import { MESES } from "./tema";
import { nucleoParaArquivo } from "./nomeDeArquivo";

// Nome de exibição: acrescenta "(inativo)" quando o atleta foi inativado no
// cadastro, mas ainda aparece por ter registro no mês.
function nomeExib(a) {
  return a && a.inativo ? `${a.nome} (inativo)` : (a ? a.nome : "");
}

// ── Geração do Excel ─────────────────────────────────────────
export async function gerarExcel({ registros, dispensas, nucleo, nucleoNome, mes, ano, nomesAtuais = null }) {
  // Em documento, o núcleo aparece pelo NOME de cadastro, nunca pela chave.
  // "IFCE" é identificador de banco; quem recebe o relatório conhece o
  // núcleo por "CFO - Ceará", e é esse nome que a coordenação edita.
  //
  // A comparação com a chave existe porque a coluna é por linha: se um dia
  // o arquivo juntar dois núcleos, cada linha continua dizendo o seu.
  const rotuloNucleo = (n) => (!n || n === nucleo ? (nucleoNome || nucleo || "") : n);

  const wb = new ExcelJS.Workbook();
  wb.creator = "Revelar Talentos";
  // Força o Excel/LibreOffice/Sheets a recalcular as fórmulas ao abrir o arquivo
  wb.calcProperties = wb.calcProperties || {};
  wb.calcProperties.fullCalcOnLoad = true;

  const HEADER = ["Data","Dia","Hora","Núcleo","Perfil","Modalidade","Nome","Email","Endereço","Lat","Lng","Maps","Foto","Precisão","Plataforma"];

  const verdeExcel = "FF00853B";
  const brancoExcel = "FFFFFFFF";

  function estilizarHeader(ws) {
    const row = ws.getRow(1);
    row.eachCell(cell => {
      cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: verdeExcel } };
      cell.font = { bold: true, color: { argb: brancoExcel }, size: 11 };
      cell.alignment = { vertical: "middle", horizontal: "center" };
    });
    row.height = 20;
  }

  function ajustarColunas(ws) {
    ws.columns.forEach(col => {
      let max = 10;
      col.eachCell({ includeEmpty: false }, cell => {
        const v = cell.value ? String(cell.value) : "";
        if (v.length > max) max = v.length;
      });
      col.width = Math.min(max + 4, 40);
    });
  }

  // Separar por semanas do mês
  function semanaDoMes(dataStr) {
    const d = new Date(dataStr.split("/").reverse().join("-") + "T12:00:00");
    const dia = d.getDate();
    if (dia <= 7) return 1;
    if (dia <= 14) return 2;
    if (dia <= 21) return 3;
    if (dia <= 28) return 4;
    return 5;
  }

  const registrosPorSemana = { 1: [], 2: [], 3: [], 4: [], 5: [] };
  registros.forEach(r => {
    const s = semanaDoMes(r.data);
    registrosPorSemana[s].push(r);
  });

  // Ordena cada semana em ordem CRONOLÓGICA (mais antigo no topo): primeiro
  // por data, depois pela hora do registro. Assim fica fácil conferir a
  // sequência do dia/semana. Converte data "dd/mm/aaaa" + hora "HH:MM[:SS]"
  // em um valor comparável.
  function instante(r) {
    const [d, m, a] = (r.data || "").split("/");
    if (!d || !m || !a) return 0;
    const iso = `${a}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`;
    // hora pode vir "HH:MM" ou "HH:MM:SS" — normaliza para HH:MM:SS
    let hora = (r.hora || "00:00").trim();
    if (/^\d{1,2}:\d{2}$/.test(hora)) hora += ":00";
    if (!/^\d{1,2}:\d{2}:\d{2}$/.test(hora)) hora = "00:00:00";
    const t = new Date(`${iso}T${hora}`);
    return isNaN(t.getTime()) ? 0 : t.getTime();
  }
  for (let s = 1; s <= 5; s++) {
    registrosPorSemana[s].sort((a, b) => instante(a) - instante(b));
  }

  // Abas Semana 1-5
  for (let s = 1; s <= 5; s++) {
    const ws = wb.addWorksheet(`Semana ${s}`);
    ws.addRow(HEADER);
    estilizarHeader(ws);
    registrosPorSemana[s].forEach(r => {
      // Registro de fim de semana: aparece no log (auditoria), mas fica
      // marcado como NÃO CONTABILIZADO — não há treino sábado/domingo, então
      // não conta na frequência (o cálculo também o ignora). Isso preserva a
      // evidência de quem registrou fora do dia de treino.
      const dow = new Date(r.data.split("/").reverse().join("-") + "T12:00:00").getDay();
      const ehFimDeSemana = (dow === 0 || dow === 6);
      const row = ws.addRow([
        r.data, r.diaSemana, r.hora, rotuloNucleo(r.nucleo), r.perfil,
        normalizarModalidade(r.modalidade), r.userName, r.email,
        r.locationName || "", r.lat, r.lng,
        { text: "Abrir Maps", hyperlink: r.locationGoogleMapsLink || "" },
        r.photoUrl ? { text: "Abrir Foto", hyperlink: r.photoUrl } : "",
        r.locationAccuracy ? Number(r.locationAccuracy).toFixed(1) : "",
        ehFimDeSemana ? "NÃO CONTABILIZADO (fim de semana)" : (r.platform || ""),
      ]);
      row.getCell(12).font = { color: { argb: "FF0000FF" }, underline: true };
      row.getCell(13).font = { color: { argb: "FF0000FF" }, underline: true };
      if (ehFimDeSemana) {
        // Linha inteira em cinza-claro + texto de aviso em vermelho.
        for (let c = 1; c <= 15; c++) {
          row.getCell(c).fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFF3F4F6" } };
          row.getCell(c).font = { ...(row.getCell(c).font || {}), color: { argb: "FF9CA3AF" }, italic: true };
        }
        row.getCell(15).font = { color: { argb: "FFDC2626" }, bold: true };
      }
    });
    ajustarColunas(ws);
  }

  // (Aba "Mês Completo" removida — os mesmos registros já aparecem nas abas
  // Semana 1-5, organizados por semana. A contagem bruta por modalidade que
  // ficava aqui incluía fim de semana; retirada junto para manter tudo
  // alinhado à regra de "só dias de treino contam".)

  // (Aba "Observações" removida — era um subconjunto da aba "Dispensas do
  // mês", que já lista TODAS as dispensas do mês, inclusive as individuais.)

  // Aba Dispensas do mês
  const wsDisp = wb.addWorksheet("Dispensas do mês");
  wsDisp.addRow(["Data","Dia","Tipo","Subtipo","Núcleo","Modalidade","Nome","Motivo","Conta Como"]);
  estilizarHeader(wsDisp);
  dispensas.forEach(d => {
    const dt = new Date(d.data + "T12:00:00");
    const ehComp = (d.subtipo || "").toLowerCase().includes("competi");
    const row = wsDisp.addRow([
      dt.toLocaleDateString("pt-BR"),
      dt.toLocaleDateString("pt-BR", { weekday: "long" }),
      d.tipo,
      d.subtipo,
      rotuloNucleo(d.nucleo),
      d.modalidade !== "Todas" ? d.modalidade : "Todos",
      d.tipo === "COLETIVA" ? "Todos" : d.tipo === "MODALIDADE" ? ("Todos de " + d.modalidade) : (d.nome || ""),
      d.motivo || "",
      ehComp ? "SIM (presença por competição)" : "SIM (justificado)",
    ]);
    // colorir a célula "Conta Como" em verde
    row.getCell(9).font = { bold: true, color: { argb: ehComp ? "FF1D6FA4" : "FF00853B" } };
    row.getCell(9).fill = { type: "pattern", pattern: "solid", fgColor: { argb: ehComp ? "FFDBEAFE" : "FFD1FAE5" } };
  });
  ajustarColunas(wsDisp);

  // ── Cálculo de Gestão de Presença ──
  // A regra de negócio vive em calculo.js — fonte única da verdade.
  // Calculado UMA vez aqui e reaproveitado pelo Excel e pelo Dashboard
  // (antes, o Dashboard recalculava tudo de novo).
  const {
    totalTreinos,
    membros: dadosGestao,
    modalidadesAtletas,
  // tempoReal desligado: numa planilha, dois atletas da mesma modalidade
  // com denominadores diferentes no mesmo mês não se explica. Aqui o dia
  // de hoje só entra depois de fechar, às 18h30.
  // `nomesAtuais` identifica a pessoa pelo nome do CADASTRO, e não pelo
  // que ficou gravado no registro de presença. É o que a tela já fazia, e
  // faltava aqui: para quem foi renomeado, a dispensa escrita com o nome
  // novo não casava com o nome velho do registro, e o abono sumia só no
  // arquivo. Mesmo mês, mesma pessoa, cinco pontos de diferença entre a
  // tela e a planilha.
  } = calcularFrequencia({ registros, dispensas, mes, ano, nomesAtuais, tempoReal: false });

  // ── Gestão de Presença ──
  const wsGestao = wb.addWorksheet("Gestão de Presença");

  const COLS_GESTAO = [
    "Nome","Perfil","Modalidade","Núcleo",
    "Treinos","Presenças","Faltas não justificadas","Justif. Coletiva","Justif. Modalidade","Justif. Individual",
    "% Absoluto","% Justif.","% Total","Classificação"
  ];
  const COL_GRUPO = 15; // coluna auxiliar oculta (O) — alimenta as fórmulas do Dashboard

  function addGestaoHeader(ws) {
    const hdr = ws.addRow(COLS_GESTAO);
    hdr.eachCell(cell => {
      cell.fill = { type:"pattern", pattern:"solid", fgColor:{ argb:"FF005C29" } };
      cell.font = { bold:true, color:{ argb:brancoExcel }, size:10 };
      cell.alignment = { vertical:"middle", horizontal:"center", wrapText:true };
      cell.border = { bottom:{ style:"medium", color:{ argb:"FF00853B" } } };
    });
    hdr.height = 30;
    ws.getCell(1, COL_GRUPO).value = "_grupo";
  }

  function addGestaoSeparator(ws, label, corArgb) {
    const totalCols = COLS_GESTAO.length;
    const row = ws.addRow([label]);
    ws.mergeCells(row.number, 1, row.number, totalCols);
    row.getCell(1).fill = { type:"pattern", pattern:"solid", fgColor:{ argb: corArgb } };
    row.getCell(1).font = { bold:true, size:11, color:{ argb:brancoExcel } };
    row.getCell(1).alignment = { horizontal:"left", vertical:"middle", indent:1 };
    row.height = 20;
  }

  function addGestaoRow(ws, u) {
    // u já vem calculado por calcularFrequencia() — não recalcula.
    const a = u;
    const cls = a.classif;
    const teto = TETO_JUSTIFICATIVA;
    // Abonos ISENTOS do teto (viagem/férias + justificado ao coordenador).
    // Entram no % integralmente, fora do limite do teto — igual ao calculo.js.
    const isentos = (a.viagem || 0) + (a.coord || 0);

    // Entradas (editáveis) + derivadas por FÓRMULA (cache = valor calculado em JS).
    // A coluna E (Treinos) é o denominador DO ATLETA — já desconta coletivas e
    // day off da modalidade (dias sem treino). Por isso as justif. coletivas
    // (coluna H) NÃO entram mais no abono: esses dias saíram do denominador.
    // A coluna J traz só as justif. individuais COMUNS (sujeitas ao teto); os
    // isentos (viagem + coord) vão somados nas fórmulas via ${isentos}.
    const row = ws.addRow([
      nomeExib(a), a.perfil, a.modalidade, rotuloNucleo(a.nucleo),
      a.treinos, a.presEfetiva, a.faltas, a.jc, a.jm, a.ji,
      a.pctAbs, a.pctJust, a.pctTotal, cls.label,
    ]);
    const r = row.number;

    // Colunas: E=Treinos(do atleta) F=Presenças(efetiva) G=Faltas
    //          H=JustCol(informativo, NÃO abona) I=JustMod J=JustIndividual(TOTAL)
    //          K=%Absoluto L=%Justif M=%Total N=Classificação
    // A coluna J agora mostra o TOTAL de justificativas individuais (comuns +
    // isentas), para o número bater com o abono que aparece no %. Como parte
    // dessas (viagem + "justificado ao coordenador" = ${isentos}) é ISENTA do
    // teto, a fórmula separa: os COMUNS sujeitos ao teto são (J - isentos), e
    // os isentos somam por fora. Assim nada conta em dobro e o número exibido
    // corresponde ao que o leitor vê no percentual.
    row.getCell(7).value  = { formula: `MAX(0,E${r}-F${r}-(MIN(I${r}+(J${r}-${isentos}),ROUND(E${r}*${teto},0))+${isentos}))`, result: a.faltas };
    row.getCell(11).value = { formula: `IF(E${r}=0,0,F${r}/E${r})`, result: a.pctAbs };
    row.getCell(12).value = { formula: `IF(E${r}=0,0,(MIN(I${r}+(J${r}-${isentos}),ROUND(E${r}*${teto},0))+${isentos})/E${r})`, result: a.pctJust };
    row.getCell(13).value = { formula: `MIN(K${r}+L${r},1)`, result: a.pctTotal };
    row.getCell(14).value = { formula: `IF(M${r}>=0.9,"🌟 Muito bom",IF(M${r}>=0.8,"✔ Bom",IF(M${r}>=0.7,"⚠ Regular",IF(M${r}>=0.5,"🔻 Ruim","⛔ Muito ruim"))))`, result: cls.label };
    [11, 12, 13].forEach(c => { row.getCell(c).numFmt = "0%"; });

    // Coluna auxiliar oculta (O) — alimenta as fórmulas do Dashboard
    row.getCell(COL_GRUPO).value = a.grupo;

    row.eachCell({ includeEmpty: false }, (cell, col) => {
      if (col > 14) return; // não estilizar a coluna auxiliar
      cell.fill = { type:"pattern", pattern:"solid", fgColor:{ argb: cls.bg } };
      cell.alignment = { vertical:"middle", horizontal:"center" };
      cell.border = { bottom:{ style:"thin", color:{ argb:"FFE5E7EB" } } };
    });
    row.getCell(1).alignment = { vertical:"middle", horizontal:"left", indent:1 };
    // Cor por faixa do % Total/Classificação aplicada ao vivo por FORMATAÇÃO
    // CONDICIONAL (aplicarCondicionais); as cores estáticas abaixo são fallback.
    row.getCell(13).font = { bold:true, color:{ argb: cls.cor } };
    row.getCell(14).font = { bold:true, color:{ argb: cls.cor } };
    row.height = 18;
    return row;
  }

  // Formatação condicional: cor da linha por faixa de % Total + Faltas em vermelho,
  // recalculada ao vivo quando o usuário edita a planilha.
  function aplicarCondicionais(ws, linhas) {
    if (!linhas.length) return;
    linhas.sort((a, b) => a - b);
    const faixas = [];
    let ini = null, prev = null;
    linhas.forEach(n => {
      if (ini === null) { ini = prev = n; }
      else if (n === prev + 1) { prev = n; }
      else { faixas.push([ini, prev]); ini = prev = n; }
    });
    faixas.push([ini, prev]);

    const fill = argb => ({ fill: { type: "pattern", pattern: "solid", bgColor: { argb } } });
    const fontCor = argb => ({ font: { bold: true, color: { argb } } });

    faixas.forEach(([a, b]) => {
      ws.addConditionalFormatting({
        ref: `A${a}:N${b}`,
        rules: [
          { type: "expression", formulae: [`$M${a}>=0.9`],                 style: fill("FFD1FAE5") },
          { type: "expression", formulae: [`AND($M${a}>=0.8,$M${a}<0.9)`], style: fill("FFDBEAFE") },
          { type: "expression", formulae: [`AND($M${a}>=0.7,$M${a}<0.8)`], style: fill("FFFFF3CD") },
          { type: "expression", formulae: [`AND($M${a}>=0.5,$M${a}<0.7)`], style: fill("FFFFE4CC") },
          { type: "expression", formulae: [`$M${a}<0.5`],                  style: fill("FFFEE2E2") },
        ],
      });
      ws.addConditionalFormatting({
        ref: `M${a}:N${b}`,
        rules: [
          { type: "expression", formulae: [`$M${a}>=0.9`],                 style: fontCor("FF00853B") },
          { type: "expression", formulae: [`AND($M${a}>=0.8,$M${a}<0.9)`], style: fontCor("FF1D6FA4") },
          { type: "expression", formulae: [`AND($M${a}>=0.7,$M${a}<0.8)`], style: fontCor("FFD97706") },
          { type: "expression", formulae: [`AND($M${a}>=0.5,$M${a}<0.7)`], style: fontCor("FFEA580C") },
          { type: "expression", formulae: [`$M${a}<0.5`],                  style: fontCor("FFB91C1C") },
        ],
      });
      ws.addConditionalFormatting({
        ref: `G${a}:G${b}`,
        rules: [
          { type: "expression", formulae: [`$G${a}>0`], style: fontCor("FFB91C1C") },
        ],
      });
    });
  }

  // dadosGestao e modalidadesAtletas já vêm calculados de calcularFrequencia()
  // — ver o bloco "Cálculo de Gestão de Presença" acima.

  // Blocos: coordenação e equipe técnica fixos; um bloco por modalidade de atleta
  // (gerado dinamicamente, com cor da paleta); "outros" no fim.
  const blocos = {
    coord:  { label: "COORDENAÇÃO E ADMINISTRATIVO", cor: "FF1E3A5F", membros: [], ordem: 0 },
    tecnico:{ label: "EQUIPE TÉCNICA E AUXILIARES",  cor: "FF065F46", membros: [], ordem: 1 },
  };
  modalidadesAtletas.forEach((mod, i) => {
    const c = corDaModalidade(modalidadesAtletas, mod);
    blocos["mod_" + mod] = {
      label: "ATLETAS — " + (mod || "").toUpperCase(),
      cor: c.cor, membros: [], ordem: 10 + i, modalidade: mod,
    };
  });
  blocos.outros = { label: "OUTROS", cor: "FF6B7280", membros: [], ordem: 999 };

  dadosGestao.forEach(u => {
    const b = classificarBloco(u);
    if (b === 1) blocos.coord.membros.push(u);
    else if (b === 2) blocos.tecnico.membros.push(u);
    else if (b === 3) {
      const chave = "mod_" + u.modalidade;
      (blocos[chave] || blocos.outros).membros.push(u);
    } else blocos.outros.membros.push(u);
  });

  addGestaoHeader(wsGestao);

  const linhasDados = [];
  Object.values(blocos).sort((a, b) => a.ordem - b.ordem).forEach(bloco => {
    if (bloco.membros.length === 0) return;
    addGestaoSeparator(wsGestao, `  ${bloco.label}  (${bloco.membros.length} membro${bloco.membros.length>1?"s":""})`, bloco.cor);
    bloco.membros.sort((a,b)=>a.nome.localeCompare(b.nome)).forEach(u => {
      const row = addGestaoRow(wsGestao, u);
      linhasDados.push(row.number);
    });
    wsGestao.addRow([]);
  });

  // Formatação condicional ao vivo (cor da linha, texto e Faltas seguem o % Total)
  aplicarCondicionais(wsGestao, linhasDados);

  // Larguras fixas (mesmo layout) — Nome..Classif. As colunas de faltas
  // ficaram um pouco mais largas para os rótulos novos ("Faltas não
  // justificadas" etc.), que quebram em 2-3 linhas no cabeçalho (wrapText).
  const larguras = [26, 20, 14, 6, 8, 9, 12, 12, 13, 12, 9, 8, 9, 14];
  larguras.forEach((w, i) => wsGestao.getColumn(i + 1).width = w);
  wsGestao.getColumn(COL_GRUPO).hidden = true;
  wsGestao.getColumn(COL_GRUPO).width = 10;
  wsGestao.views = [{ state:"frozen", ySplit:1 }];

  // ── Dashboard (ligado à Gestão por fórmula; recálculo validado) ──
  const wsInd = wb.addWorksheet("📊 Dashboard");

  function mergeTitle(ws, r, c1, c2, text, fillArgb, fontSz=13) {
    ws.mergeCells(r, c1, r, c2);
    const cell = ws.getCell(r, c1);
    cell.value = text;
    cell.fill = { type:"pattern", pattern:"solid", fgColor:{ argb: fillArgb } };
    cell.font = { bold:true, size:fontSz, color:{ argb:brancoExcel } };
    cell.alignment = { horizontal:"center", vertical:"middle" };
    ws.getRow(r).height = 26;
    return cell;
  }
  function sectionBar(ws, r, text, fillArgb="FF005C29") {
    ws.mergeCells(r, 1, r, 12);
    const cell = ws.getCell(r, 1);
    cell.value = text;
    cell.fill = { type:"pattern", pattern:"solid", fgColor:{ argb: fillArgb } };
    cell.font = { bold:true, size:11, color:{ argb:brancoExcel } };
    cell.alignment = { horizontal:"left", vertical:"middle", indent:1 };
    ws.getRow(r).height = 20;
  }
  function kpiBlock(ws, r, c, label, value, valArgb="FF00853B", bgArgb="FFF9FAFB", numFmt=null) {
    ws.mergeCells(r,   c, r,   c+1);
    ws.mergeCells(r+1, c, r+1, c+1);
    const vCell = ws.getCell(r, c);
    vCell.value = value;
    if (numFmt) vCell.numFmt = numFmt;
    vCell.fill = { type:"pattern", pattern:"solid", fgColor:{ argb: bgArgb } };
    vCell.font = { bold:true, size:20, color:{ argb: valArgb } };
    vCell.alignment = { horizontal:"center", vertical:"middle" };
    ws.getRow(r).height = 34;
    const lCell = ws.getCell(r+1, c);
    lCell.value = label;
    lCell.fill = { type:"pattern", pattern:"solid", fgColor:{ argb: bgArgb } };
    lCell.font = { size:9, color:{ argb:"FF6B7280" } };
    lCell.alignment = { horizontal:"center", vertical:"middle" };
    ws.getRow(r+1).height = 16;
    return vCell;
  }
  const colLetra = n => { let s=""; while(n>0){ const m=(n-1)%26; s=String.fromCharCode(65+m)+s; n=Math.floor((n-1)/26);} return s; };

  // Fórmulas que apontam para a Gestão de Presença (validadas via recálculo real)
  const GP = "'Gestão de Presença'";
  // Gestão: C=Modalidade, E=Treinos, K=%Abs, M=%Total, O=grupo
  const fAtletas  = m => m ? `COUNTIFS(${GP}!O:O,"ATLETA",${GP}!C:C,"${m}")` : `COUNTIF(${GP}!O:O,"ATLETA")`;
  const fMediaTot = m => m ? `IFERROR(AVERAGEIFS(${GP}!M:M,${GP}!O:O,"ATLETA",${GP}!C:C,"${m}"),0)` : `IFERROR(AVERAGEIF(${GP}!O:O,"ATLETA",${GP}!M:M),0)`;
  const fMediaAbs = `IFERROR(AVERAGEIF(${GP}!O:O,"ATLETA",${GP}!K:K),0)`;
  const fTier = (m, lo, hi) => {
    const base = m ? `${GP}!O:O,"ATLETA",${GP}!C:C,"${m}"` : `${GP}!O:O,"ATLETA"`;
    // Critérios numéricos por CONCATENAÇÃO (">="&0.9) — à prova de localidade:
    // o motor formata o número na localidade vigente (ex.: pt-BR ">=0,9"),
    // mantendo a comparação numérica. Texto fixo ">=0.9" zera em Excel pt-BR.
    if (hi === null) return `COUNTIFS(${base},${GP}!M:M,">="&${lo})`;
    if (lo === null) return `COUNTIFS(${base},${GP}!M:M,"<"&${hi})`;
    return `COUNTIFS(${base},${GP}!M:M,">="&${lo},${GP}!M:M,"<"&${hi})`;
  };

  // Helpers JS — preenchem o cache das fórmulas (valores corretos antes do recálculo)
  // Sem recálculo: reaproveita o que calcularFrequencia() já produziu.
  const atletasCalc = dadosGestao.filter(a => a.grupo === "ATLETA");
  const nAtl = atletasCalc.length || 1;
  const inTier = (a, lo, hi) => (lo === null || a.pctTotal >= lo) && (hi === null || a.pctTotal < hi);
  const countTier = (arr, lo, hi) => arr.filter(a => inTier(a, lo, hi)).length;
  const media = (arr, sel) => arr.length ? arr.reduce((s, a) => s + sel(a), 0) / arr.length : 0;
  // Barra de progresso (■) para fração 0..1 — usada como cache do REPT
  const barra = (frac, largura) => "■".repeat(Math.max(0, Math.round((frac || 0) * largura)));

  // larguras das 12 colunas do dashboard (mesmo layout)
  [26,8,12,10,12,10,12,10,12,10,12,10].forEach((w,i)=>wsInd.getColumn(i+1).width=w);

  let R = 1;

  // ══ TÍTULO ══
  mergeTitle(wsInd, R, 1, 12,
    `  DASHBOARD DE FREQUÊNCIA  ·  REVELAR TALENTOS  ·  ${MESES[mes].toUpperCase()} / ${ano}  ·  NÚCLEO: ${rotuloNucleo()}`,
    "FF00853B", 14);
  R += 2;

  // ══ KPIs ══
  sectionBar(wsInd, R, "  📋  VISÃO GERAL DO MÊS", "FF1E3A5F"); R++;
  kpiBlock(wsInd, R, 1, "Dias de Treino",   { formula: `MAX(${GP}!E:E)`, result: totalTreinos }, "FF00853B", "FFF0FFF4");
  const refAtletas = colLetra(3) + R; // C{kpiRow} — célula "Total de Atletas"
  kpiBlock(wsInd, R, 3, "Total de Atletas", { formula: fAtletas(), result: atletasCalc.length }, "FF7C3AED", "FFF5F3FF");
  kpiBlock(wsInd, R, 5, "Média % Absoluto", { formula: fMediaAbs, result: media(atletasCalc, a=>a.pctAbs) }, "FF0891B2", "FFE0F2FE", "0%");
  kpiBlock(wsInd, R, 7, "Média % Total",    { formula: fMediaTot(), result: media(atletasCalc, a=>a.pctTotal) }, "FFD97706", "FFFEFCE8", "0%");
  R += 3;
  wsInd.addRow([]); R++;

  // ══ TABELA POR MODALIDADE ══
  sectionBar(wsInd, R, "  📊  FREQUÊNCIA POR MODALIDADE", "FF065F46"); R++;
  const modHdr = wsInd.addRow(["Modalidade","Atletas","Média % Total","Frequência (barra = média % Total)","","","","","","","",""]);
  modHdr.eachCell((cell,i) => {
    if (i > 4) return;
    cell.fill = { type:"pattern", pattern:"solid", fgColor:{ argb:"FF047857" } };
    cell.font = { bold:true, color:{ argb:brancoExcel }, size:10 };
    cell.alignment = { horizontal:"center", vertical:"middle" };
    cell.border = { bottom:{ style:"medium", color:{ argb:"FF00853B" } } };
  });
  wsInd.mergeCells(modHdr.number, 4, modHdr.number, 12);
  modHdr.getCell(4).alignment = { horizontal:"left", vertical:"middle", indent:1 };
  modHdr.height = 22; R++;

  // Modalidades presentes nos atletas (derivadas), com cores da paleta dinâmica.
  const modOrdem = Array.from(new Set(atletasCalc.map(a => a.modalidade).filter(Boolean))).sort();
  const modColors = {}, modTxtCol = {};
  modOrdem.forEach(mod => {
    const c = corDaModalidade(modOrdem, mod);
    modColors[mod] = c.bg;
    modTxtCol[mod] = c.txt;
  });

  modOrdem.forEach(mod => {
    const arr = atletasCalc.filter(a => a.modalidade === mod);
    if (arr.length === 0) return;
    const mediaTot = media(arr, a=>a.pctTotal);
    const row = wsInd.addRow([mod, "", "", "", "", "", "", "", "", "", "", ""]);
    const rn = row.number;
    row.getCell(2).value = { formula: fAtletas(mod),  result: arr.length };
    row.getCell(3).value = { formula: fMediaTot(mod), result: mediaTot };
    row.getCell(3).numFmt = "0%";
    const bg = modColors[mod] || "FFFFFFFF";
    const tc = modTxtCol[mod] || "FF374151";
    row.eachCell((cell,i)=>{
      if (i>3) return;
      cell.fill = { type:"pattern", pattern:"solid", fgColor:{ argb:bg } };
      cell.alignment = { horizontal:"center", vertical:"middle" };
      cell.border = { bottom:{ style:"thin", color:{ argb:"FFE5E7EB" } } };
    });
    row.getCell(1).font = { bold:true, color:{ argb:tc } };
    row.getCell(1).alignment = { horizontal:"left", indent:1 };
    row.getCell(3).font = { bold:true, size:13, color:{ argb:tc } };
    // GRÁFICO DE BARRAS (fórmula REPT, recalcula com a média % Total da linha)
    wsInd.mergeCells(rn, 4, rn, 12);
    row.getCell(4).value = { formula: `IFERROR(REPT("■",ROUND(C${rn}*24,0)),"")`, result: barra(mediaTot, 24) };
    row.getCell(4).font = { size:11, color:{ argb:tc } };
    row.getCell(4).alignment = { horizontal:"left", vertical:"middle", indent:1 };
    row.getCell(4).fill = { type:"pattern", pattern:"solid", fgColor:{ argb:bg } };
    row.height = 22; R++;
  });
  R += 2;

  // ══ CLASSIFICAÇÃO GERAL (5 faixas com barra REPT) ══
  sectionBar(wsInd, R, "  🏅  CLASSIFICAÇÃO DOS ATLETAS", "FF92400E"); R++;
  const faixas = [
    { label:"🌟 Muito bom (90–100%)", cor:"FF00853B", bg:"FFF0FDF4", lo:0.9,  hi:null },
    { label:"✔ Bom (80–89%)",          cor:"FF1D6FA4", bg:"FFEFF6FF", lo:0.8,  hi:0.9 },
    { label:"⚠ Regular (70–79%)",      cor:"FFD97706", bg:"FFFEFCE8", lo:0.7,  hi:0.8 },
    { label:"🔻 Ruim (50–69%)",        cor:"FFEA580C", bg:"FFFFF1E6", lo:0.5,  hi:0.7 },
    { label:"⛔ Muito ruim (≤49%)",    cor:"FFB91C1C", bg:"FFFEF2F2", lo:null, hi:0.5 },
  ];
  faixas.forEach(f => {
    const cnt = countTier(atletasCalc, f.lo, f.hi);
    const pct = cnt / nAtl;
    const row = wsInd.addRow([f.label, "", "", "", "", "", "", "", "", "", "", ""]);
    const rn = row.number;
    row.getCell(2).value = { formula: fTier(null, f.lo, f.hi), result: cnt };
    row.getCell(3).value = { formula: `IFERROR(B${rn}/${refAtletas},0)`, result: pct };
    row.getCell(3).numFmt = "0%";
    wsInd.mergeCells(rn, 4, rn, 12);
    // GRÁFICO DE BARRAS (fórmula REPT sobre o % da faixa)
    row.getCell(4).value = { formula: `IFERROR(REPT("■",ROUND(C${rn}*30,0)),"")`, result: barra(pct, 30) };
    [1,2,3,4].forEach(c => { row.getCell(c).fill = { type:"pattern", pattern:"solid", fgColor:{ argb:f.bg } }; });
    row.getCell(1).font = { bold:true, size:10, color:{ argb:f.cor } };
    row.getCell(2).font = { bold:true, size:11, color:{ argb:f.cor } };
    row.getCell(2).alignment = { horizontal:"center" };
    row.getCell(3).font = { bold:true, size:11, color:{ argb:f.cor } };
    row.getCell(3).alignment = { horizontal:"center" };
    row.getCell(4).font = { size:11, color:{ argb:f.cor } };
    row.getCell(4).alignment = { horizontal:"left", indent:1 };
    row.height = 22; R++;
  });
  R += 2;

  // ══ DESTAQUE — MUITO BOM ≥90% (snapshot) ══
  sectionBar(wsInd, R, "  🌟  DESTAQUE — MUITO BOM (≥ 90%)", "FF92400E"); R++;
  // Usa a classificação OFICIAL (a.classif, de calculo.js) em vez de
  // refazer o corte com >= 0.90 aqui — garante que quem aparece como
  // "🌟 Muito bom" nas telas apareça também nesta lista (inclusive casos
  // de arredondamento na fronteira, ex: 89,6% → 90%).
  const excelentes = atletasCalc.filter(a => a.classif.label.includes("Muito bom")).sort((a,b)=>b.pctTotal-a.pctTotal || a.nome.localeCompare(b.nome));
  if (excelentes.length === 0) {
    wsInd.mergeCells(R,1,R,12);
    wsInd.getCell(R,1).value = "Nenhum atleta na faixa Muito bom (≥90%) neste mês.";
    wsInd.getCell(R,1).font = { italic:true, color:{ argb:"FF9CA3AF" } };
    R++;
  } else {
    const h = wsInd.addRow(["Nome","Modalidade","Pres. efetiva","Competição","% Absoluto","% Total","","","","","",""]);
    h.eachCell((cell,i)=>{ if(i>6)return; cell.font={bold:true,color:{argb:brancoExcel}}; cell.fill={type:"pattern",pattern:"solid",fgColor:{argb:"FF92400E"}}; cell.alignment={horizontal:"center"}; }); R++;
    excelentes.forEach(a => {
      const row = wsInd.addRow([a.nome, a.modalidade, a.presEfetiva, a.competicao, a.pctAbs, a.pctTotal, "","","","","",""]);
      row.getCell(5).numFmt = "0%"; row.getCell(6).numFmt = "0%";
      row.getCell(5).font = { bold:true, color:{ argb:"FF1D6FA4" } };
      row.getCell(6).font = { bold:true, color:{ argb:"FF059669" } };
      row.eachCell((cell,i)=>{ if(i>6)return; cell.alignment={horizontal:"center"}; });
      row.getCell(1).alignment = { horizontal:"left", indent:1 };
      row.height = 20; R++;
    });
  }
  R += 2;

  // ══ ABAIXO DE 80% (snapshot, do menor para o maior) ══
  // 80% é a marca que o programa usa: abaixo disso o atleta não pode ser
  // selecionado para apoio financeiro em eventos.
  sectionBar(wsInd, R, "  ⚠  ABAIXO DE 80%  (ordenado do menor % para o maior)", "FFB45309"); R++;
  const abaixo = atletasCalc.filter(a => a.pctTotal < 0.8).sort((a,b)=>a.pctTotal-b.pctTotal);
  if (abaixo.length === 0) {
    wsInd.mergeCells(R,1,R,12);
    wsInd.getCell(R,1).value = "Nenhum atleta abaixo de 80%.";
    wsInd.getCell(R,1).font = { italic:true, color:{ argb:"FF9CA3AF" } };
    R++;
  } else {
    const h = wsInd.addRow(["Nome","Modalidade","Pres. efetiva","Faltas não justif.","% Absoluto","% Total","Classificação","","","","",""]);
    h.eachCell((cell,i)=>{ if(i>7)return; cell.font={bold:true,color:{argb:brancoExcel}}; cell.fill={type:"pattern",pattern:"solid",fgColor:{argb:"FFB45309"}}; cell.alignment={horizontal:"center"}; }); R++;
    abaixo.forEach(a => {
      const row = wsInd.addRow([a.nome, a.modalidade, a.presEfetiva, a.faltas, a.pctAbs, a.pctTotal, a.classif.label, "","","","",""]);
      row.getCell(5).numFmt = "0%"; row.getCell(6).numFmt = "0%";
      const bgAt = modColors[a.modalidade] || "FFFEFCE8";
      row.eachCell((cell,i)=>{ if(i>7)return; cell.fill={type:"pattern",pattern:"solid",fgColor:{argb:bgAt}}; cell.alignment={horizontal:"center"}; });
      row.getCell(1).alignment = { horizontal:"left", indent:1 };
      row.getCell(6).font = { bold:true, color:{ argb:a.classif.cor } };
      row.getCell(7).font = { bold:true, color:{ argb:a.classif.cor } };
      row.height = 20; R++;
    });
  }
  R += 2;

  // ══ RODAPÉ (1 linha) ══
  sectionBar(wsInd, R, "  📝  RESUMO", "FF374151"); R++;
  wsInd.mergeCells(R,1,R,12);
  const foot = wsInd.getCell(R,1);
  // Contagens de dispensas para o resumo (a aba "Observações" que as
  // definia foi removida; recalculamos aqui, direto das dispensas).
  const nColetivas = dispensas.filter(d => d.tipo === "COLETIVA").length;
  const nModalidade = dispensas.filter(d => d.tipo === "MODALIDADE").length;
  foot.value =
    `${MESES[mes]}/${ano} · Núcleo ${nucleoNome || nucleo}  —  ${totalTreinos} dias de treino (seg–sex)  ·  ` +
    `${nColetivas} dispensa(s) coletiva(s)  ·  ${nModalidade} por modalidade  ·  ` +
    `teto de justificativa: ${(TETO_JUSTIFICATIVA*100).toFixed(0)}% dos dias  ·  competição conta como presença`;
  foot.font = { size:11, color:{ argb:"FF1A1A1A" } };
  foot.fill = { type:"pattern", pattern:"solid", fgColor:{ argb:"FFF9FAFB" } };
  foot.alignment = { wrapText:true, vertical:"middle", indent:1 };
  wsInd.getRow(R).height = 34;

  // Gerar buffer e download
  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `Relatorio_${nucleoParaArquivo(nucleoNome, nucleo)}_${ano}-${String(mes + 1).padStart(2, "0")}.xlsx`;
  link.click();
  URL.revokeObjectURL(url);
  return link.download;
}

// O relatório por modalidade saiu do Excel e passou a ser só PDF
// (ver gerarPDFModalidade em pdfRelatorio.js). O gerador Excel foi
// removido junto para não ficar código morto sustentando um botão
// que não existe mais.
